import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEvents } from "../lib/api";
import { Calendar, List, MapPin, Clock, Ticket, Users } from "lucide-react";
import EventCalendar from "../components/EventCalendar";
import RsvpModal from "../components/RsvpModal";

export default function Events() {
  const [view, setView] = useState("calendar");
  const [rsvpEvent, setRsvpEvent] = useState(null);
  // Fetch ALL events (past + upcoming) for the calendar; the list view filters to upcoming.
  const { data: allEvents = [], isLoading } = useQuery({
    queryKey: ["events-all"],
    queryFn: () => getEvents(false),
  });
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = allEvents.filter((e) => e.date >= today);

  return (
    <>
      <section className="bg-[#FDFBF7] border-b border-black/10">
        <div className="container-x py-20 md:py-28">
          <span className="overline text-[#6B21A8]">Events & Gatherings</span>
          <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold tracking-tight leading-none">Show up.<br /><span className="italic">Grow together.</span></h1>
          <p className="mt-8 max-w-2xl text-black/70 text-lg">
            Retreats, service days, career fairs, meetings, and celebrations — designed for young men, families, mentors, and community partners.
          </p>
          <div className="mt-8 inline-flex rounded-md border border-black/15 p-1 bg-white" data-testid="events-view-toggle">
            <button
              onClick={() => setView("calendar")}
              data-testid="view-calendar"
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition ${
                view === "calendar" ? "bg-[#6B21A8] text-white" : "text-black/70 hover:text-black"
              }`}
            >
              <Calendar className="h-4 w-4" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition ${
                view === "list" ? "bg-[#6B21A8] text-white" : "text-black/70 hover:text-black"
              }`}
            >
              <List className="h-4 w-4" /> List
            </button>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-14 md:py-16">
          {isLoading ? (
            <p className="text-black/60">Loading events…</p>
          ) : view === "calendar" ? (
            <EventCalendar events={allEvents} />
          ) : upcoming.length === 0 ? (
            <p className="text-black/60">No upcoming events — check the calendar for past events, or add one from the admin dashboard.</p>
          ) : (
            <div className="grid gap-8" data-testid="events-list">
              {upcoming.map((e) => {
                const d = new Date(e.date + "T12:00:00");
                return (
                  <article key={e.id} data-testid={`event-item-${e.id}`} className="grid gap-8 md:grid-cols-12 border-b border-black/10 pb-8">
                    <div className="md:col-span-3">
                      <div className="border-2 border-[#6B21A8] p-4 text-center inline-block">
                        <p className="font-display text-5xl font-bold text-[#6B21A8] leading-none">{d.getDate()}</p>
                        <p className="mt-2 overline text-black/70">{d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</p>
                      </div>
                    </div>
                    <div className="md:col-span-5">
                      <p className="overline text-[#6B21A8]">{e.category}</p>
                      <h2 className="mt-3 font-display text-3xl font-semibold text-black leading-tight">{e.title}</h2>
                      <div className="mt-4 space-y-2 text-sm text-black/70">
                        {e.time && <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {e.time}</p>}
                        {e.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {e.location}</p>}
                      </div>
                      {e.description && <p className="mt-4 text-black/80">{e.description}</p>}
                      {e.rsvp_enabled !== false && (
                        <button
                          onClick={() => setRsvpEvent(e)}
                          data-testid={`list-rsvp-${e.id}`}
                          className={`mt-5 inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                            e.ticket_price > 0 ? "bg-[#F5B90A] text-black" : "bg-[#6B21A8] text-white"
                          }`}
                        >
                          {e.ticket_price > 0 ? <><Ticket className="h-4 w-4" /> Get ticket · ${e.ticket_price?.toFixed(0)}</> : <><Users className="h-4 w-4" /> RSVP</>}
                        </button>
                      )}
                    </div>
                    {e.image && (
                      <div className="md:col-span-4">
                        <div className="aspect-video overflow-hidden border border-black/10">
                          <img src={e.image} alt="" className="h-full w-full object-cover" />
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
      {rsvpEvent && <RsvpModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
    </>
  );
}
